import * as React from "react";

import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

import "./PublicLoading.css";

/**
 * What an invitee looks at while a public page fetches.
 *
 * <p>These pages used to render the string "Loading…" in muted grey. That is
 * the cheapest possible answer on the one surface where the audience has never
 * seen the product before, and it is worse again inside an embed, where the
 * host's own site is polished and our iframe is four grey words.</p>
 *
 * <p>In its place, {@link PublicLoadingLoop}: four colour calendar and schedule
 * icons juggled on a shallow ellipse above a hairline track.</p>
 *
 * <p>Every public link shows the loop, the ones that open on a calendar
 * included. Those additionally reserve the grid with {@link BookingPageSkeleton}
 * <b>below</b> the loop: the two answer different questions and neither answers
 * both. The skeleton holds the grid's real geometry so nothing jumps when the
 * slots land; the loop says the page is working.</p>
 *
 * <p>Stacked, never overlaid, and the loop goes on top. Artwork sitting ON the
 * grid pulls the eye to the middle of a card that is about to redraw
 * completely, which is the one place nothing is going to happen. Above it, the
 * colour reads as status and the grey below it reads as the shape of what is
 * coming.</p>
 *
 * <p>Host-facing screens are deliberately untouched. This is what a stranger
 * sees, not what the person editing the link sees.</p>
 *
 * <p>The motion is entirely CSS. It used to be a React interval swapping one
 * icon for the next, which meant a timer running behind every public page for
 * the whole of a fetch; the orbit needs no state at all, so there is nothing to
 * start, clear, or leak.</p>
 */

/* ---------------------------------------------------------------------------
   The icons.

   Drawn here rather than taken from the emoji font. System emoji cost nothing
   to ship, but the art belongs to the viewer's OS, so the same loader is three
   different drawings across Mac, Windows and Android and none of them is ours.
   These are the brand palette, one weight, one corner radius, and identical
   everywhere.

   Four distinct silhouettes, not four calendars: a grid, a single date, a
   circle and an hourglass. At this size the shape is the only thing that survives,
   so two icons that differ merely in detail would read as one icon flickering.

   No tick in the set. A green check while the page is still working reads as
   "done" to anyone glancing at it.
--------------------------------------------------------------------------- */
const BLUE = "#2779A7";
const TEAL = "#49C5B6";
const CORAL = "#DF6C4F";
const YELLOW = "#ECD06F";
const RING = "#94A3B8";
const PAGE = "#FFFFFF";

/** The month: a page with a blue head and a grid of days. */
function MonthIcon() {
  return (
    <svg viewBox="0 0 32 32" className="scheda-icon">
      <rect x="8.8" y="1.8" width="2.6" height="5.4" rx="1.3" fill={RING} />
      <rect x="20.6" y="1.8" width="2.6" height="5.4" rx="1.3" fill={RING} />
      <rect x="3.4" y="4.4" width="25.2" height="24.2" rx="4.2" fill={BLUE} />
      {/* The body is painted over the blue card rather than clipped out of it,
          which keeps the head's top corners round without a clipPath. */}
      <path d="M3.4 11.6 h25.2 v12.8 a4.2 4.2 0 0 1 -4.2 4.2 h-16.8 a4.2 4.2 0 0 1 -4.2 -4.2 z" fill={PAGE} />
      <rect x="3.4" y="4.4" width="25.2" height="24.2" rx="4.2" fill="none" stroke={BLUE} strokeWidth="1.6" />
      {[0, 1, 2].map((c) => (
        <rect key={`a${c}`} x={7.4 + c * 6.6} y="14.6" width="3.4" height="3.4" rx="1.1" fill={TEAL} />
      ))}
      <rect x="7.4" y="20.4" width="3.4" height="3.4" rx="1.1" fill={TEAL} />
      <rect x="14" y="20.4" width="3.4" height="3.4" rx="1.1" fill={CORAL} />
    </svg>
  );
}

/** The date: the same page, one day taken. */
function DateIcon() {
  return (
    <svg viewBox="0 0 32 32" className="scheda-icon">
      <rect x="8.8" y="1.8" width="2.6" height="5.4" rx="1.3" fill={RING} />
      <rect x="20.6" y="1.8" width="2.6" height="5.4" rx="1.3" fill={RING} />
      <rect x="3.4" y="4.4" width="25.2" height="24.2" rx="4.2" fill={CORAL} />
      <path d="M3.4 11.6 h25.2 v12.8 a4.2 4.2 0 0 1 -4.2 4.2 h-16.8 a4.2 4.2 0 0 1 -4.2 -4.2 z" fill={PAGE} />
      <rect x="3.4" y="4.4" width="25.2" height="24.2" rx="4.2" fill="none" stroke={CORAL} strokeWidth="1.6" />
      <rect x="10.6" y="14.4" width="10.8" height="9.4" rx="2.6" fill={TEAL} />
    </svg>
  );
}

/** The time: a clock, hands at ten past ten. */
function ClockIcon() {
  return (
    <svg viewBox="0 0 32 32" className="scheda-icon">
      <circle cx="16" cy="16.6" r="12.2" fill={YELLOW} />
      <circle cx="16" cy="16.6" r="9.4" fill={PAGE} />
      <circle cx="16" cy="16.6" r="12.2" fill="none" stroke={BLUE} strokeWidth="1.6" />
      <path
        d="M16 16.6 V 10.4 M16 16.6 L 20.6 19.4"
        stroke={CORAL}
        strokeWidth="2.2"
        strokeLinecap="round"
        fill="none"
      />
      <circle cx="16" cy="16.6" r="1.5" fill={BLUE} />
    </svg>
  );
}

/** The wait: an hourglass, running. */
function HourglassIcon() {
  return (
    <svg viewBox="0 0 32 32" className="scheda-icon">
      {/* Wider than it first was. The calendars and the clock fill the 32 box
          edge to edge; an hourglass drawn to its natural proportions read a
          weight lighter than the other three and broke the set. */}
      <path d="M8 6.6 H24 L17.6 16.5 L24 26.4 H8 L14.4 16.5 Z" fill={PAGE} stroke={BLUE} strokeWidth="1.7" strokeLinejoin="round" />
      <path d="M10.9 9.4 H21.1 L16 16.5 Z" fill={YELLOW} />
      <path d="M16 19.5 L21.4 24.3 H10.6 Z" fill={YELLOW} />
      <rect x="5.4" y="3.2" width="21.2" height="3.2" rx="1.6" fill={BLUE} />
      <rect x="5.4" y="25.4" width="21.2" height="3.2" rx="1.6" fill={BLUE} />
    </svg>
  );
}

const ICONS = [MonthIcon, DateIcon, ClockIcon, HourglassIcon] as const;

/**
 * How long a fetch may take before anything is painted at all.
 *
 * <p>A loading state that appears instantly is worse than none: a fast
 * connection shows a flash and then a redraw, which reads as a stutter.</p>
 */
const REVEAL_MS = 120;

/**
 * How long it stays once it has appeared, whatever the fetch does.
 *
 * <p>One full revolution of the orbit. The delay alone was not enough: a public
 * link resolves in 240-550ms, so the loop painted for ~150ms and vanished,
 * which reads as a flicker rather than as a loader. A page is held back by at
 * most the difference, and only on a page that was already slow enough to show
 * a loader at all.</p>
 */
const MIN_VISIBLE_MS = 650;

/**
 * Whether the loading visual should be on screen, given whether the page is
 * still fetching.
 *
 * <p>Two thresholds, not one. Nothing is shown for the first {@link REVEAL_MS},
 * and once shown it stays for {@link MIN_VISIBLE_MS} even if the fetch has
 * already finished. A fetch that beats the first threshold shows nothing at
 * all, which is the point of it.</p>
 *
 * <p>Callers must gate their <b>ready and error</b> branches on this too, not
 * only their loading branch. During the hold the fetch has finished, so a page
 * that only swapped its loading condition would render the loader and the
 * finished page at the same time.</p>
 */
export function useLoadingWindow(active: boolean): boolean {
  const [shown, setShown] = React.useState(false);
  const shownAt = React.useRef(0);

  React.useEffect(() => {
    if (active) {
      if (shown) return;
      const id = window.setTimeout(() => {
        shownAt.current = Date.now();
        setShown(true);
      }, REVEAL_MS);
      return () => window.clearTimeout(id);
    }
    if (!shown) return;
    const left = MIN_VISIBLE_MS - (Date.now() - shownAt.current);
    if (left <= 0) {
      setShown(false);
      return;
    }
    const id = window.setTimeout(() => setShown(false), left);
    return () => window.clearTimeout(id);
  }, [active, shown]);

  return shown;
}

/**
 * The loading state for every public, invitee-facing page.
 *
 * @param label what a screen reader hears, announced once
 */
export function PublicLoadingLoop({
  className,
  label = "Loading",
}: {
  className?: string;
  label?: string;
}) {
  return (
    <div className={cn("scheda-reveal flex flex-col items-center py-8", className)}>
      {/* Order matters here and is not cosmetic: the stylesheet spreads the four
          around the ellipse by nth-child, and the fallback for a browser with
          no offset-path shows :first-child alone. */}
      <div className="scheda-orbit" aria-hidden="true">
        {ICONS.map((Icon, n) => (
          <span key={n}>
            <Icon />
          </span>
        ))}
      </div>
      <div className="scheda-track mt-3" aria-hidden="true">
        <i />
      </div>
      {/* One announcement of the state. The icons are hidden from the tree, so
          nobody is read a list of drawings. */}
      <span role="status" className="sr-only">
        {label}
      </span>
    </div>
  );
}

/**
 * The booking card's own shape, painted behind the loop on the pages that open
 * on a calendar.
 *
 * <p>Deliberately colourless. Everything here is one grey at one radius: the
 * grid is a placeholder for something that has not arrived, and a placeholder
 * that draws the eye is worse than an empty box. The colour on this card
 * belongs to the loop underneath, which is the part that is actually saying
 * something.</p>
 *
 * <p>Three week rows is an approximation, not a promise: the real calendar
 * renders only the weeks that carry slots, so the count is not knowable before
 * the fetch returns. What the skeleton does hold exactly is the cell geometry:
 * seven columns, square cells, the same gap and the same radius, so the grid
 * does not resize under the invitee when the slots arrive.</p>
 */
export function BookingPageSkeleton({ className }: { className?: string }) {
  return (
    <div className={cn("scheda-reveal", className)}>
      <Skeleton className="h-3.5 w-1/2 motion-reduce:animate-none" />
      <Skeleton className="mt-2.5 h-2.5 w-1/3 motion-reduce:animate-none" />

      <div className="mt-5 grid grid-cols-7 gap-1.5">
        {Array.from({ length: 21 }, (_, n) => (
          <Skeleton key={n} className="aspect-square rounded-xl motion-reduce:animate-none" />
        ))}
      </div>

      <div className="mt-5 flex flex-wrap gap-2">
        {Array.from({ length: 4 }, (_, n) => (
          <Skeleton key={n} className="h-[30px] w-[76px] rounded-lg motion-reduce:animate-none" />
        ))}
      </div>
    </div>
  );
}
